import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useNavigation } from '@react-navigation/native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { OffersLoadRecovery } from 'features/offer/components/offers-load-recovery';
import { useEnableLoyaltyServiceOffers } from 'features/offer/hooks/use-enable-loyalty-service-offers';
import { useLoyaltyServiceOffer } from 'features/offer/hooks/use-loyalty-service-offer';
import { useStoreContext } from 'features/store';

import { CardModal } from 'components/card-modal';
import { toaster } from 'components/toaster/toaster';
import { OfferNoStorePreview } from 'experiments/deeplinks-flow-feature/treatment/features/offer/components/offer-no-store-preview';
import { applyOfferStoreRestrictions } from 'experiments/deeplinks-flow-feature/treatment/features/offer/utils/apply-offer-store-restrictions';
import { applyStoreRestrictionUnavailableMessage } from 'experiments/deeplinks-flow-feature/treatment/features/offer/utils/apply-store-restriction-unavailable-message';
import { buildStoreRestrictionLocatorParams } from 'experiments/deeplinks-flow-feature/treatment/features/offer/utils/build-store-restriction-locator-params';
import { navigateToOffers } from 'experiments/deeplinks-flow-feature/treatment/features/offer/utils/navigate-to-offers';
import { navigateToUnavailableOffersFeed } from 'experiments/deeplinks-flow-feature/treatment/features/offer/utils/navigate-to-unavailable-offers-feed';
import { replaceOfferDetailWithStoreLocator } from 'experiments/deeplinks-flow-feature/treatment/features/offer/utils/replace-offer-detail-with-store-locator';
import { useControlNoStoreStoreLocatorRedirect } from 'experiments/deeplinks-flow-feature/treatment/features/store/hooks/use-control-no-store-store-locator-redirect';
import {
  useDeeplinksFlowExperiment,
  useExposeDeeplinksFlowExperiment,
} from 'experiments/deeplinks-flow-feature/use-deeplinks-flow-experiment';
import { useExposeRenderReadyOffersRewardsExperiment } from 'experiments/use-render-ready-offers-rewards-experiment';
import IncentiveDetails from 'pages/loyalty/loyalty-incentives-components/incentive-details/incentive-details';
import { incentiveDetailsCardFillStyle } from 'pages/loyalty/loyalty-incentives-components/incentive-details/incentive-details.styled';
import LoyaltyLoader from 'pages/loyalty/loyalty-loader/loyalty-loader';
import { useGetCmsAndLoyaltyOffers } from 'pages/loyalty/loyalty-offers/use-get-cms-and-loyalty-offers';
import { useGetIncentive } from 'pages/loyalty/loyalty-offers/use-get-incentive';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { LoyaltyEngineId, LoyaltyOffer, appOfferToLoyaltyOffer } from 'state/loyalty/types';
import { routes } from 'utils/routing';

export default function OfferDetailRoute() {
  const { noStoreSelected } = useStoreContext();
  const { loyaltyEngineId } = useLocalSearchParams<{ loyaltyEngineId: LoyaltyEngineId }>();
  const {
    isEnabled: isDeeplinksFlowEnabled,
    isLoading: isExperimentLoading,
  } = useDeeplinksFlowExperiment();
  const {
    enabled: enableLoyaltyServiceOffers,
    eligibleForExposure,
  } = useEnableLoyaltyServiceOffers();
  const closeToOffers = useCloseToOffersOnBackDismiss(
    isDeeplinksFlowEnabled && !isExperimentLoading
  );

  useExposeRenderReadyOffersRewardsExperiment(eligibleForExposure && !noStoreSelected);
  useExposeDeeplinksFlowExperiment(noStoreSelected && !isExperimentLoading);

  if (isExperimentLoading) {
    return (
      <CardModal alwaysShowHeader isLoading wrappedChildrenStyle={incentiveDetailsCardFillStyle}>
        <LoyaltyLoader />
      </CardModal>
    );
  }

  if (noStoreSelected) {
    return isDeeplinksFlowEnabled ? (
      <TreatmentOfferNoStore loyaltyEngineId={loyaltyEngineId} onDismiss={closeToOffers} />
    ) : (
      <ControlOfferNoStoreStoreLocatorRedirect loyaltyEngineId={loyaltyEngineId} />
    );
  }

  if (enableLoyaltyServiceOffers || isDeeplinksFlowEnabled) {
    return (
      <ServiceOfferDetail
        loyaltyEngineId={loyaltyEngineId}
        isDeeplinksFlowEnabled={isDeeplinksFlowEnabled}
        onDismiss={closeToOffers}
      />
    );
  }

  return <LegacyOfferDetail loyaltyEngineId={loyaltyEngineId} />;
}

function useCloseToOffersOnBackDismiss(enabled: boolean): () => void {
  const router = useRouter();
  const navigation = useNavigation();
  const isNavigatingToOffersRef = useRef(false);

  const closeToOffers = useCallback(() => {
    if (isNavigatingToOffersRef.current) {
      return;
    }

    isNavigatingToOffersRef.current = true;
    navigateToOffers(router);
  }, [router]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const unsubscribe = navigation.addListener('beforeRemove', event => {
      const actionType = event.data.action.type;
      const isBackAction = actionType === 'GO_BACK' || actionType === 'POP';

      // Swipe-down and hardware-back dismissals bypass CardModalHeader onDismiss.
      if (!isBackAction || isNavigatingToOffersRef.current) {
        return;
      }

      event.preventDefault();
      closeToOffers();
    });

    return unsubscribe;
  }, [closeToOffers, enabled, navigation]);

  return closeToOffers;
}

/**
 * Treatment no-store branch: preview the deeplinked offer, then send the guest
 * to the minimal locator. `redirectUrl` returns here so availability is
 * evaluated against the restaurant they pick.
 */
function TreatmentOfferNoStore({
  loyaltyEngineId,
  onDismiss,
}: {
  loyaltyEngineId: LoyaltyEngineId;
  onDismiss: () => void;
}) {
  const router = useRouter();
  // No store means store restrictions cannot be evaluated yet; skipping them
  // keeps store-restricted offers previewable instead of resolving to null.
  const {
    selectedIncentive,
    isIncentivesLoading,
    hasSettled,
    queryError,
    refreshOffer,
  } = useLoyaltyServiceOffer({
    loyaltyEngineId,
    includeStoreRestricted: true,
  });

  const handleSelectRestaurant = () => {
    router.navigate({
      pathname: routes.minimalStoreLocator,
      params: {
        redirectUrl: routes.loyaltyOfferDetails.replace(':loyaltyEngineId', loyaltyEngineId),
        offerEngineId: loyaltyEngineId,
        // The preview already loaded the offer without a store, so its
        // restrictions are known here and let the locator disable restaurants
        // that would bounce the guest straight back as unavailable.
        ...buildStoreRestrictionLocatorParams(selectedIncentive ?? {}),
      },
    });
  };

  if (queryError) {
    return (
      <CardModal
        alwaysShowHeader
        onDismiss={onDismiss}
        wrappedChildrenStyle={incentiveDetailsCardFillStyle}
      >
        <OffersLoadRecovery offline={false} onRetry={refreshOffer} />
      </CardModal>
    );
  }

  if (isIncentivesLoading || !hasSettled) {
    return (
      <CardModal
        alwaysShowHeader
        isLoading
        onDismiss={onDismiss}
        wrappedChildrenStyle={incentiveDetailsCardFillStyle}
      >
        <LoyaltyLoader />
      </CardModal>
    );
  }

  // There is no offer to preview, so the treatment CTA has nothing to attach to.
  if (!selectedIncentive) {
    return (
      <TreatmentOfferNoStoreLocatorRedirect
        loyaltyEngineId={loyaltyEngineId}
        onDismiss={onDismiss}
      />
    );
  }

  return (
    <OfferNoStorePreview
      offer={selectedIncentive}
      onDismiss={onDismiss}
      onSelectRestaurant={handleSelectRestaurant}
    />
  );
}

/**
 * Treatment fallback for a deeplinked offer that resolves to nothing without a
 * restaurant: leave the empty offer modal behind and hand the guest the full
 * locator in its place.
 */
function TreatmentOfferNoStoreLocatorRedirect({
  loyaltyEngineId,
  onDismiss,
}: {
  loyaltyEngineId: LoyaltyEngineId;
  onDismiss: () => void;
}) {
  const router = useRouter();
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    if (hasRedirectedRef.current) {
      return;
    }
    hasRedirectedRef.current = true;
    replaceOfferDetailWithStoreLocator(
      router,
      routes.loyaltyOfferDetails.replace(':loyaltyEngineId', loyaltyEngineId)
    );
  }, [loyaltyEngineId, router]);

  return (
    <CardModal
      alwaysShowHeader
      isLoading
      onDismiss={onDismiss}
      wrappedChildrenStyle={incentiveDetailsCardFillStyle}
    >
      <LoyaltyLoader />
    </CardModal>
  );
}

function ControlOfferNoStoreStoreLocatorRedirect({
  loyaltyEngineId,
}: {
  loyaltyEngineId: LoyaltyEngineId;
}) {
  useControlNoStoreStoreLocatorRedirect(
    routes.loyaltyOfferDetails.replace(':loyaltyEngineId', loyaltyEngineId)
  );

  return (
    <CardModal alwaysShowHeader isLoading wrappedChildrenStyle={incentiveDetailsCardFillStyle}>
      <LoyaltyLoader />
    </CardModal>
  );
}

function ServiceOfferDetail({
  loyaltyEngineId,
  isDeeplinksFlowEnabled,
  onDismiss,
}: {
  loyaltyEngineId: LoyaltyEngineId;
  isDeeplinksFlowEnabled: boolean;
  onDismiss: () => void;
}) {
  const dispatch = useAppDispatch();
  const shouldRefetchOffers = useAppSelector(selectors.loyalty.selectShouldRefetchOffers);
  const { store } = useStoreContext();
  const {
    offerListItem: storeScopedOffer,
    selectedIncentive: storeScopedSelectedIncentive,
    isIncentivesLoading: isStoreScopedLoading,
    hasSettled: hasStoreScopedSettled,
    queryError: storeScopedError,
    refreshOffer: refreshStoreScopedOffer,
  } = useLoyaltyServiceOffer({
    loyaltyEngineId,
    includeStoreRestricted: isDeeplinksFlowEnabled,
  });
  const {
    offerListItem: storeAgnosticOffer,
    isIncentivesLoading: isStoreAgnosticLoading,
    hasSettled: hasStoreAgnosticSettled,
    queryError: storeAgnosticError,
    refreshOffer: refreshStoreAgnosticOffer,
  } = useLoyaltyServiceOffer({
    loyaltyEngineId,
    includeStoreRestricted: true,
    omitStoreId: true,
    skip: !isDeeplinksFlowEnabled,
  });

  const selectedIncentive = useMemo(() => {
    if (!isDeeplinksFlowEnabled) {
      return storeScopedSelectedIncentive;
    }

    // A scoped response cannot carry storeRestrictionMode/availableStoreIds.
    // Do not decide that it is redeemable until the agnostic response has
    // supplied those fields and the selected restaurant has been checked.
    const offerWithRestrictions = applyOfferStoreRestrictions(storeScopedOffer, storeAgnosticOffer);
    if (!offerWithRestrictions) {
      return undefined;
    }

    return appOfferToLoyaltyOffer(
      applyStoreRestrictionUnavailableMessage(offerWithRestrictions, store?.number)
    );
  }, [
    isDeeplinksFlowEnabled,
    store?.number,
    storeAgnosticOffer,
    storeScopedOffer,
    storeScopedSelectedIncentive,
  ]);

  const queryError = storeScopedError ?? (isDeeplinksFlowEnabled ? storeAgnosticError : undefined);
  const isIncentivesLoading =
    isStoreScopedLoading || (isDeeplinksFlowEnabled && isStoreAgnosticLoading);
  const hasSettled =
    hasStoreScopedSettled && (!isDeeplinksFlowEnabled || hasStoreAgnosticSettled) && !queryError;
  const refreshOffer = useCallback(async () => {
    if (isDeeplinksFlowEnabled) {
      await Promise.all([refreshStoreScopedOffer(), refreshStoreAgnosticOffer()]);
      return;
    }
    await refreshStoreScopedOffer();
  }, [isDeeplinksFlowEnabled, refreshStoreAgnosticOffer, refreshStoreScopedOffer]);

  useEffect(() => {
    if (shouldRefetchOffers) {
      void refreshOffer();
      dispatch(actions.loyalty.setShouldRefetchOffers(false));
    }
  }, [dispatch, refreshOffer, shouldRefetchOffers]);

  if (queryError) {
    return (
      <CardModal
        alwaysShowHeader
        onDismiss={isDeeplinksFlowEnabled ? onDismiss : undefined}
        wrappedChildrenStyle={incentiveDetailsCardFillStyle}
      >
        <OffersLoadRecovery offline={false} onRetry={refreshOffer} />
      </CardModal>
    );
  }

  return (
    <OfferDetailContent
      isIncentivesLoading={isIncentivesLoading}
      hasSettled={hasSettled}
      isDeeplinksFlowEnabled={isDeeplinksFlowEnabled}
      loyaltyEngineId={loyaltyEngineId}
      onDismiss={isDeeplinksFlowEnabled ? onDismiss : undefined}
      selectedIncentive={selectedIncentive}
    />
  );
}

function LegacyOfferDetail({ loyaltyEngineId }: { loyaltyEngineId: LoyaltyEngineId }) {
  const shouldRefetchOffers = useAppSelector(selectors.loyalty.selectShouldRefetchOffers);
  const { refreshOffers } = useGetCmsAndLoyaltyOffers();
  const { selectedIncentive, isIncentivesLoading } = useGetIncentive({ loyaltyEngineId });

  useEffect(() => {
    if (shouldRefetchOffers) {
      refreshOffers();
    }
  }, [refreshOffers, shouldRefetchOffers]);

  return (
    <OfferDetailContent
      isIncentivesLoading={isIncentivesLoading}
      hasSettled={!isIncentivesLoading}
      isDeeplinksFlowEnabled={false}
      loyaltyEngineId={loyaltyEngineId}
      onDismiss={undefined}
      selectedIncentive={selectedIncentive}
      showUnavailableDefaultMessage={selectedIncentive?.isUnavailable}
    />
  );
}

function OfferDetailContent({
  hasSettled,
  isDeeplinksFlowEnabled,
  isIncentivesLoading,
  loyaltyEngineId,
  onDismiss,
  selectedIncentive,
  showUnavailableDefaultMessage,
}: {
  hasSettled: boolean;
  isDeeplinksFlowEnabled: boolean;
  isIncentivesLoading: boolean;
  loyaltyEngineId: LoyaltyEngineId;
  onDismiss?: () => void;
  selectedIncentive?: LoyaltyOffer & { isUnavailable?: boolean };
  showUnavailableDefaultMessage?: boolean;
}) {
  const router = useRouter();
  const incentiveExists = !!selectedIncentive;
  // A settled null offer is the store-unavailable signal, not an invalid id:
  // the loyalty service returns null for an offer the selected restaurant
  // cannot redeem even with store restrictions skipped, so there is nothing
  // left to carry an inlineAlertMessage. The feed recovers the card content
  // store-agnostically and falls back to the invalid-offer toast if the offer
  // does not exist at all.
  const shouldShowUnavailableCard =
    isDeeplinksFlowEnabled &&
    hasSettled &&
    !isIncentivesLoading &&
    (!selectedIncentive || Boolean(selectedIncentive.inlineAlertMessage));

  useExposeDeeplinksFlowExperiment(shouldShowUnavailableCard);

  useEffect(() => {
    if (shouldShowUnavailableCard) {
      navigateToUnavailableOffersFeed(router, loyaltyEngineId);
      return;
    }

    if (!isIncentivesLoading && !incentiveExists) {
      if (!router.canDismiss()) {
        return router.replace(routes.offers);
      }
      router.dismiss();
      toaster.negative(t('incentiveFeedbackInvalidStore', { incentiveType: t('offer') }));
    }
  }, [incentiveExists, isIncentivesLoading, loyaltyEngineId, router, shouldShowUnavailableCard]);

  // Missing/unavailable treatment offers redirect to the feed before any detail modal is shown.
  if (isIncentivesLoading || !incentiveExists || shouldShowUnavailableCard) {
    return (
      <CardModal
        alwaysShowHeader
        isLoading
        onDismiss={onDismiss}
        wrappedChildrenStyle={incentiveDetailsCardFillStyle}
      >
        <LoyaltyLoader />
      </CardModal>
    );
  }

  return (
    <IncentiveDetails
      engineIncentivesMap={{}}
      onDismiss={onDismiss}
      selectedIncentive={selectedIncentive}
      showUnavailableDefaultMessage={showUnavailableDefaultMessage}
    />
  );
}
